import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, Scale } from "lucide-react";

const money = (n) => (Number(n || 0)).toFixed(2);

export const TimeLeavePanel = ({ companyId, employees, start, end, canEdit }) => {
  const [codes, setCodes] = useState([]);
  const [hours, setHours] = useState({});
  const [form, setForm] = useState({ employee_id: "", date: start || "", code: "REGHRS", hours_worked: "", hours_paid: "", source: "manual" });
  const [balEmp, setBalEmp] = useState("");
  const [bal, setBal] = useState(null);
  const [adj, setAdj] = useState({ balance_type: "vacation", hours: "", reason: "" });

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/time-codes`).then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, [companyId]);

  const loadHours = useCallback(() => {
    if (!companyId || !start || !end) return;
    api.get(`/companies/${companyId}/timesheets/payroll-hours?start=${start}&end=${end}`)
      .then((r) => setHours(r.data.employees || {})).catch(() => {});
  }, [companyId, start, end]);
  useEffect(() => { loadHours(); }, [loadHours]);

  const loadBalances = (eid) => {
    setBalEmp(eid); setBal(null);
    if (eid) api.get(`/companies/${companyId}/employees/${eid}/leave-balances`).then((r) => setBal(r.data)).catch(() => {});
  };

  const empName = (id) => { const e = employees.find((x) => x.employee_id === id); return e ? `${e.first_name} ${e.last_name}` : id; };

  const addEntry = async (allowDup = false) => {
    if (!form.employee_id || !form.date) { toast.error("Employee and date required"); return; }
    try {
      await api.post(`/companies/${companyId}/timesheets`, {
        employee_id: form.employee_id, date: form.date, code: form.code,
        hours_worked: Number(form.hours_worked || 0), hours_paid: Number(form.hours_paid || 0),
        source: form.source, allow_duplicate: allowDup });
      toast.success("Time entry saved"); loadHours();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(e.response.data?.detail?.message + "\n\nAdd anyway?")) return addEntry(true);
      } else toast.error(apiErr(e));
    }
  };

  const submitAdj = async () => {
    if (!balEmp || !adj.reason.trim() || adj.hours === "") { toast.error("Employee, hours and reason required"); return; }
    try {
      const { data } = await api.post(`/companies/${companyId}/employees/${balEmp}/leave-adjustment`,
        { balance_type: adj.balance_type, hours: Number(adj.hours), reason: adj.reason });
      if (data.warning) toast.warning(data.warning); else toast.success("Balance adjusted");
      loadBalances(balEmp); setAdj({ ...adj, hours: "", reason: "" });
    } catch (e) {
      toast.error(e?.response?.status === 409 ? (e.response.data?.detail?.message || "Blocked by policy") : apiErr(e));
    }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="time-leave-panel">
      {/* Coded time / leave entry */}
      {canEdit && (
        <div className="border border-border bg-white p-5">
          <div className="flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4" /><h3 className="text-sm font-bold uppercase tracking-widest">Add time / leave entry</h3></div>
          <div className="flex flex-wrap items-end gap-2">
            <select data-testid="tl-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="border border-input px-2 py-1.5 text-sm min-w-40">
              <option value="">Employee…</option>
              {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
            </select>
            <input data-testid="tl-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-input px-2 py-1.5 text-sm" />
            <select data-testid="tl-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="border border-input px-2 py-1.5 text-sm min-w-44">
              {codes.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}{c.review_required ? " · Review" : ""}</option>)}
            </select>
            <input data-testid="tl-worked" type="number" placeholder="Worked" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} className="w-20 border border-input px-2 py-1.5 text-sm text-right" />
            <input data-testid="tl-paid" type="number" placeholder="Paid" value={form.hours_paid} onChange={(e) => setForm({ ...form, hours_paid: e.target.value })} className="w-20 border border-input px-2 py-1.5 text-sm text-right" />
            <button data-testid="tl-add" onClick={() => addEntry(false)} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Add</button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Worked vs paid hours are tracked separately. EI-insurable & ROE hours are derived from the code's treatment (overtime counts as actual hours, unpaid leave adds none). Money flows through existing earning codes.</p>
        </div>
      )}

      {/* Payroll-hours preview */}
      <div className="border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4" /><h3 className="text-sm font-bold uppercase tracking-widest">Payroll hours — this period</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tl-hours-table">
            <thead><tr className="border-b border-border text-left text-[10px] font-mono uppercase text-muted-foreground">
              <th className="px-3 py-2">Employee</th><th className="px-3 py-2 text-right">Worked</th><th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Leave</th><th className="px-3 py-2 text-right">Insurable</th><th className="px-3 py-2 text-right">ROE</th><th className="px-3 py-2">Flags</th></tr></thead>
            <tbody>
              {Object.keys(hours).length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No time recorded this period.</td></tr>}
              {Object.entries(hours).map(([eid, a]) => (
                <tr key={eid} data-testid={`tl-hours-${eid}`} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">{empName(eid)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(a.worked_hours)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(a.paid_hours)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(a.leave_hours)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(a.insurable_hours)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(a.roe_hours)}</td>
                  <td className="px-3 py-2">
                    {(a.warnings || []).map((w, i) => <span key={i} title={w.detail} className="mr-1 text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1 py-0.5">{w.kind}</span>)}
                    {(a.review_codes || []).length > 0 && <span title={`Review: ${a.review_codes.join(", ")}`} className="text-[9px] font-mono uppercase border border-violet-500 bg-violet-50 text-violet-700 px-1 py-0.5">review</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leave balances + adjustment */}
      <div className="border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-3"><Scale className="h-4 w-4" /><h3 className="text-sm font-bold uppercase tracking-widest">Leave balances</h3></div>
        <select data-testid="tl-bal-employee" value={balEmp} onChange={(e) => loadBalances(e.target.value)} className="border border-input px-2 py-1.5 text-sm min-w-40">
          <option value="">Select employee…</option>
          {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
        </select>
        {bal && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="tl-balances">
              {Object.keys(bal.balances).length === 0 && <span className="text-sm text-muted-foreground">No leave balances yet.</span>}
              {Object.entries(bal.balances).map(([t, h]) => (
                <span key={t} className={`text-xs font-mono uppercase border px-2 py-1 ${h < 0 ? "border-red-500 bg-red-50 text-red-700" : "border-border"}`}>{t}: {money(h)}h</span>
              ))}
            </div>
            {canEdit && (
              <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
                <select data-testid="tl-adj-type" value={adj.balance_type} onChange={(e) => setAdj({ ...adj, balance_type: e.target.value })} className="border border-input px-2 py-1.5 text-sm">
                  {["vacation", "sick", "lieu", "personal"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input data-testid="tl-adj-hours" type="number" placeholder="+/- hours" value={adj.hours} onChange={(e) => setAdj({ ...adj, hours: e.target.value })} className="w-24 border border-input px-2 py-1.5 text-sm text-right" />
                <input data-testid="tl-adj-reason" placeholder="Reason (required)" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="flex-1 min-w-40 border border-input px-2 py-1.5 text-sm" />
                <button data-testid="tl-adj-apply" onClick={submitAdj} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Adjust</button>
              </div>
            )}
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Ledger</div>
            <div className="max-h-40 overflow-y-auto text-xs" data-testid="tl-ledger">
              {(bal.transactions || []).slice().reverse().map((t) => (
                <div key={t.txn_id} className="flex justify-between border-b border-border py-1">
                  <span>{t.date} · {t.balance_type} · {t.direction}{t.reason ? ` — ${t.reason}` : ""}</span>
                  <span className={`tabular-nums ${t.hours < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.hours > 0 ? "+" : ""}{money(t.hours)}h</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
